import React from "react";

/*
 * Location and the document glyph ship as real assets in the repo, and their
 * path data is identical to the reference artboard (location-point.svg is the
 * same pin, just translated). Both already paint with currentColor, so the
 * icon tile tints them. Prefer them over the traced copies.
 */
import { ReactComponent as LocationAsset } from "../../../../images/icons/location-point.svg";
import { ReactComponent as FileEditAsset } from "../../../../images/icons/file-edit.svg";
import { ReactComponent as InfoAsset } from "../../../../images/icons/Info.svg";

/*
 * Icons lifted verbatim from providerPayRequestDashboardNew/UI-reference.svg.
 *
 * Each glyph lives inside a known 44x44 box in the source artboard, so the path
 * data is copied untouched and the box is expressed as the viewBox. That keeps
 * the shapes bit-identical to the design with no coordinate math. Colours are
 * currentColor so the surrounding icon tile controls the tint.
 */

/*
 * `box` is the glyph's 44x44 tile in the source artboard, so the svg must be
 * rendered at 44 for the glyph to come out at its designed size — inside that
 * tile the artwork itself is only ~15-19 units. Rendering smaller shrinks the
 * glyph proportionally and it reads as a half-size icon.
 */
const Icon = ({ box, size = 44, children }) => (
  <svg
    width={size}
    height={size}
    viewBox={box}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const LocationIcon = () => <LocationAsset width="22" height="22" />;

export const DocumentIcon = () => <FileEditAsset width="20" height="20" />;

export const DescriptionIcon = ({ size }) => (
  <Icon box="1039 887 44 44" size={size}>
    <path
      d="M1063.75 899.833H1055.5C1055.01 899.833 1054.55 900.026 1054.2 900.37C1053.86 900.714 1053.67 901.18 1053.67 901.667V916.333C1053.67 916.819 1053.86 917.286 1054.2 917.63C1054.55 917.973 1055.01 918.167 1055.5 918.167H1066.5C1066.99 918.167 1067.45 917.973 1067.8 917.63C1068.14 917.286 1068.33 916.819 1068.33 916.333V904.417L1063.75 899.833Z"
      stroke="currentColor"
      strokeWidth="1.33319"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1062.83 899.833V903.5C1062.83 903.986 1063.03 904.452 1063.37 904.796C1063.71 905.14 1064.18 905.333 1064.67 905.333H1068.33"
      stroke="currentColor"
      strokeWidth="1.33319"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1059.17 906.25H1057.33"
      stroke="currentColor"
      strokeWidth="1.33319"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1064.67 909.917H1057.33"
      stroke="currentColor"
      strokeWidth="1.33319"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1064.67 913.583H1057.33"
      stroke="currentColor"
      strokeWidth="1.33319"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
);

export const PaperclipIcon = ({ size }) => (
  <Icon box="1048 1091 44 44" size={size}>
    <path d="M1075.62 1104.57C1076.63 1105.02 1077.42 1105.85 1077.81 1106.89C1078.2 1107.92 1078.17 1109.07 1077.71 1110.08L1073.61 1119.19C1073.29 1119.89 1072.85 1120.52 1072.29 1121.05C1071.73 1121.57 1071.07 1121.98 1070.36 1122.25C1069.64 1122.53 1068.88 1122.65 1068.11 1122.63C1067.35 1122.61 1066.59 1122.43 1065.9 1122.12C1065.2 1121.8 1064.57 1121.36 1064.04 1120.8C1063.52 1120.24 1063.11 1119.58 1062.84 1118.87C1062.56 1118.15 1062.44 1117.39 1062.46 1116.62C1062.48 1115.86 1062.66 1115.1 1062.97 1114.41L1065.71 1108.33L1067.23 1109.01L1064.49 1115.09C1064.04 1116.1 1064 1117.24 1064.39 1118.28C1064.79 1119.31 1065.57 1120.15 1066.58 1120.6C1067.59 1121.05 1068.73 1121.09 1069.77 1120.7C1070.8 1120.3 1071.64 1119.52 1072.09 1118.51L1076.19 1109.39C1076.33 1109.09 1076.4 1108.77 1076.41 1108.44C1076.42 1108.11 1076.37 1107.79 1076.25 1107.48C1076.14 1107.17 1075.96 1106.89 1075.73 1106.65C1075.51 1106.41 1075.24 1106.22 1074.94 1106.09C1074.64 1105.95 1074.32 1105.88 1073.99 1105.87C1073.66 1105.86 1073.33 1105.91 1073.03 1106.03C1072.72 1106.14 1072.44 1106.32 1072.2 1106.54C1071.96 1106.77 1071.77 1107.04 1071.63 1107.34L1067.53 1116.46C1067.44 1116.66 1067.43 1116.89 1067.51 1117.1C1067.59 1117.3 1067.75 1117.47 1067.95 1117.56C1068.15 1117.65 1068.38 1117.66 1068.59 1117.58C1068.79 1117.5 1068.96 1117.34 1069.05 1117.14L1072.81 1108.78L1074.33 1109.47L1070.57 1117.83C1070.3 1118.43 1069.8 1118.9 1069.18 1119.14C1068.56 1119.37 1067.87 1119.35 1067.26 1119.08C1066.66 1118.81 1066.19 1118.31 1065.95 1117.69C1065.72 1117.07 1065.74 1116.38 1066.01 1115.77L1070.11 1106.65C1070.57 1105.65 1071.4 1104.86 1072.44 1104.47C1073.47 1104.08 1074.62 1104.11 1075.62 1104.57Z" fill="currentColor" />
  </Icon>
);

export const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline
      points="9 6 15 12 9 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InfoIcon = () => <InfoAsset width="14" height="14" color="#6b6c6d" />;

export const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline
      points="6 9 12 15 18 9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Pay Request Type, Name and Shift Type all share the document glyph in the design.
export const FIELD_ICONS = {
  location: <LocationIcon size={40} />,
  payRequestType: <DocumentIcon size={40} />,
  payRequestName: <DocumentIcon size={40} />,
  shiftType: <DocumentIcon size={40} />,
};

export const FileIcon = DocumentIcon;
